// Sentry initialization — opt-in via env var. To enable:
//   1. bun add @sentry/react
//   2. set VITE_SENTRY_DSN=https://...@o0.ingest.sentry.io/0 in apps/web/.env
//   3. (optional) set VITE_SENTRY_ENVIRONMENT=production|staging|local
//
// We dynamic-import the SDK so the package is OPTIONAL — if it isn't
// installed yet, this file is a no-op. That keeps `bun install` working
// before the user opts in. Once you `bun add @sentry/react`, just having
// the DSN set lights it up.

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const ENVIRONMENT = (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ??
  (import.meta.env.DEV ? "local" : "production");
const RELEASE = import.meta.env.VITE_APP_VERSION as string | undefined;

export async function initSentry(): Promise<void> {
  if (!DSN) return; // Off by default — silent no-op.

  try {
    // Optional dep: don't let Vite try to resolve the path at module-graph
    // build time (it errors hard otherwise). The /* @vite-ignore */ skips
    // analysis, and the variable specifier dodges static detection so the
    // bundler doesn't include it. At runtime, if @sentry/react isn't
    // installed, the import throws and we catch below.
    const sentryPkg = "@sentry/react";
    const Sentry = (await import(/* @vite-ignore */ sentryPkg)) as any;

    Sentry.init({
      dsn: DSN,
      environment: ENVIRONMENT,
      release: RELEASE,
      // Most apps want both:
      //   - sample 100% of errors (we're a small ops team, every one matters)
      //   - sample a slice of perf traces to keep volume reasonable
      tracesSampleRate: import.meta.env.DEV ? 0 : 0.1,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      // Skip noisy expected errors — auth redirects, AbortControllers, etc.
      ignoreErrors: [
        "AbortError",
        "Non-Error promise rejection captured",
        // Better Auth throws this on 401 during boot before login page mounts.
        "User not authenticated",
      ],
      beforeSend(event, hint) {
        const err = hint?.originalException;
        // Drop network errors caused by the user being offline — the sync
        // engine already surfaces them, sending them to Sentry doubles up.
        if (err instanceof TypeError && /Failed to fetch|NetworkError/.test(err.message ?? "")) {
          return null;
        }
        return event;
      },
    });
  } catch (err) {
    // Either the package isn't installed yet, or init threw. Don't let
    // observability break the app.
    console.warn("[sentry] init skipped:", err);
  }
}

// Optional: lightweight manual capture so call sites don't need to know
// whether Sentry is loaded. If it's off, this is a no-op.
export async function captureError(err: unknown, context?: Record<string, unknown>): Promise<void> {
  if (!DSN) return;
  try {
    // Same trick as initSentry: variable specifier + @vite-ignore so Vite
    // doesn't try to resolve @sentry/react at build time.
    const sentryPkg = "@sentry/react";
    const Sentry = (await import(/* @vite-ignore */ sentryPkg)) as any;
    Sentry.captureException(err, { extra: context });
  } catch {
    /* same as above — never let logging break the app */
  }
}
